import {
  SYNC_NOTIFICATION_CATEGORY,
  SyncNotification,
  SyncNotificationCategory,
} from '@/types/admin-sync-notification';
import { Checkbox } from 'antd';
import dayjs from 'dayjs';
import { AppBreadcrumb, AppContainer, AppPagination, AppTabs } from '../atoms';
import { RA2NDatePicker } from '../organisms';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import AppButton from '../atoms/AppButton';
import { SyncNotificationTable } from '../molecules';
import { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

/** The shared picker speaks 和暦 display strings; state and API speak YYYY-MM-DD. */
const toPickerValue = (iso: string): string => (iso ? convGtJDate(dayjs(iso)) : '');

const CATEGORY_OPTIONS: { label: string; value: SyncNotificationCategory }[] = [
  { label: '同期失敗', value: SYNC_NOTIFICATION_CATEGORY.SYNC_FAILED },
  { label: '状態の不一致', value: SYNC_NOTIFICATION_CATEGORY.RECONCILE_DRIFT },
  { label: 'レセプト特定不可', value: SYNC_NOTIFICATION_CATEGORY.SYNC_UNRESOLVED },
  { label: '返戻取消の対象なし', value: SYNC_NOTIFICATION_CATEGORY.RETURN_CANCEL_NOT_FOUND },
  { label: '返戻通知の保留', value: SYNC_NOTIFICATION_CATEGORY.RETURN_BLOCKED },
  { label: '管理者権限の変更', value: SYNC_NOTIFICATION_CATEGORY.ADMIN_ACCESS_CHANGED },
];

/** What each inbox is for — a reviewer opening 不一致 has to know re-pushing is not the answer. */
const CATEGORY_HINT: Record<SyncNotificationCategory, string> = {
  [SYNC_NOTIFICATION_CATEGORY.SYNC_FAILED]:
    'Shinsaへの送信が失敗したレセプトです。再同期で解消する場合があります。',
  [SYNC_NOTIFICATION_CATEGORY.RECONCILE_DRIFT]:
    '毎日の照合でRAHと審査側の状態が食い違っていたものです。送信自体は成功しているため再同期では解消しません。内容を確認して原因を特定してください。',
  [SYNC_NOTIFICATION_CATEGORY.SYNC_UNRESOLVED]:
    '審査側から届いた通知（返戻・入金など）で、対象レセプトを特定できなかったものです。通知は再送しても取り込めません。対象を特定したうえで、審査側の画面で操作をやり直してください。',
  [SYNC_NOTIFICATION_CATEGORY.RETURN_CANCEL_NOT_FOUND]:
    '治療院からの返戻取消依頼を送信しましたが、審査側に対象レセプトが存在しなかったものです。審査側で対象を特定できる状態にしたうえで、再同期してください。',
  [SYNC_NOTIFICATION_CATEGORY.RETURN_BLOCKED]:
    '返戻取消の保護期間中に届いた返戻通知です。審査状況には「返戻」を記録していますが、レセプトは確定のまま保留しています。保護期間が終了すると、以降の返戻通知は通常どおり処理されます。',
  [SYNC_NOTIFICATION_CATEGORY.ADMIN_ACCESS_CHANGED]:
    '管理者の審査システム・開示システムの利用可否を変更した記録です。変更はすでに反映されているため、対応は不要です。送信に失敗した場合は「同期失敗」に表示されます。',
};

interface AdminSyncNotificationListTemplateProps {
  data: SyncNotification[];
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  /** Per-row 再同期 — re-push only this record. */
  onRetry: (aggregate: string, aggregateId: string) => void;
  /** Top 全て再同期 — re-push every failed record. */
  onSyncAll: () => void;
  /** 既読にする — mark one notification read. */
  onMarkRead: (id: number) => void;
  /** 明細CSV — download the rezepts behind one 状態の不一致 notice. */
  onDownloadCsv?: (id: number) => void;
  /** Notification id whose CSV is being fetched. */
  downloadingId?: number | null;
  unreadOnly: boolean;
  onUnreadOnlyChange: (value: boolean) => void;
  /** 受信日 range (BD 5.0), YYYY-MM-DD, '' when unset. */
  from: string;
  to: string;
  onDateRangeChange: (from: string, to: string) => void;
  /** Which inbox is shown. */
  category: SyncNotificationCategory;
  onCategoryChange: (value: SyncNotificationCategory) => void;
  /** Unread per inbox — the number on each tab. Undefined while the count request is in flight. */
  unreadCounts?: Record<SyncNotificationCategory, number>;
  /** Any mutation in flight — disables row + header actions. */
  isBusy?: boolean;
  /** Bulk re-sync in flight (spinner on the top button). */
  isSyncingAll?: boolean;
  retryingAggregateId?: string | null;
  readingId?: number | null;
  breadcrumbItems: BreadcrumbItemType[];
  isLoading?: boolean;
  /** BD 10.0 — show サイト区分 (more than one site is in view). */
  showSiteColumn?: boolean;
  /** BD 17.0 — records 全て再同期 would act on in this inbox. Undefined while the count is in flight. */
  resyncPending?: number;
}

const AdminSyncNotificationListTemplate = ({
  data,
  isLoading,
  current,
  total,
  pageSize,
  onChange,
  onRetry,
  onSyncAll,
  onMarkRead,
  onDownloadCsv,
  downloadingId,
  unreadOnly,
  onUnreadOnlyChange,
  from,
  to,
  onDateRangeChange,
  category,
  onCategoryChange,
  unreadCounts,
  isBusy,
  isSyncingAll,
  retryingAggregateId,
  readingId,
  breadcrumbItems,
  showSiteColumn,
  resyncPending,
}: AdminSyncNotificationListTemplateProps) => {
  // 全て再同期 belongs to the 同期失敗 inbox alone, and for a reason BD 13.0 does not touch: bulk re-pushing
  // cannot fix drift, an inbound message RAH could not place, or a hold that expires on its own. The other
  // four inboxes carry action_type = null for exactly that reason.
  const isFailedInbox = category === SYNC_NOTIFICATION_CATEGORY.SYNC_FAILED;

  return (
    <AppContainer>
      <main className="space-y-5 px-4">
        <AppBreadcrumb items={breadcrumbItems} />
        <div className="flex items-center justify-between">
          <h3 className="text-xl">同期通知</h3>
          {isFailedInbox && (
            <AppButton
              type="primary"
              loading={isSyncingAll}
              // BD 17.0 — enabled when at least one record needs a re-push, counted server-side across every
              // page. `data.length === 0` used to stand in for that and was wrong twice over: it went by the
              // CURRENT PAGE, and it counted rows that carry no 再同期 at all.
              disabled={isBusy || !resyncPending}
              onClick={onSyncAll}
            >
              全て再同期
            </AppButton>
          )}
        </div>

        {/* One card holds the whole inbox — tab, hint, filters and list read as a single unit. */}
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          {/* Each category is its own inbox, so it gets a tab rather than hiding behind a dropdown. */}
          <AppTabs
            className="app-inbox-tabs"
            activeKey={category}
            onChange={value => onCategoryChange(value as SyncNotificationCategory)}
            items={CATEGORY_OPTIONS.map(option => ({
              key: option.value,
              label: (
                <span className="app-inbox-tabs__label">
                  {option.label}
                  {/* Unread badge — hidden at 0, so a clean inbox stays quiet */}
                  {(unreadCounts?.[option.value] ?? 0) > 0 && (
                    <span className="app-inbox-tabs__badge">{unreadCounts?.[option.value]}</span>
                  )}
                </span>
              ),
            }))}
          />

          <p className="m-0 text-xs text-gray-500">{CATEGORY_HINT[category]}</p>

          <div className="flex flex-wrap items-center gap-4">
            {/* 受信日 range — RA2NDatePicker, the picker the rest of the app uses: 和暦 input and display.
                Each end bounds the other so the pair cannot cross.

                PLACEHOLDER IS EXPLICITLY 「日付を入力」, matching the other date inputs in 管理 screens.
                It has to be passed: RA2NDatePicker falls back to 元号00年00月00日 on `placeholder ?? …`,
                so omitting the prop prints a format mask instead of the prompt.

                LABELLED 受信日, not 発生日時: BD 5.0 names the search condition 「受信日検索」 while BD 7.0
                names the column 「発生日時」. Same field (created_at), two names — the spec's, kept as-is. */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">受信日</span>
              <RA2NDatePicker
                value={toPickerValue(from)}
                onChange={value => onDateRangeChange(dateValueToIso(value) ?? '', to)}
                endDate={to || undefined}
                placeholder="日付を入力"
                className="w-[190px]"
              />
              <span className="text-sm text-gray-500">〜</span>
              <RA2NDatePicker
                value={toPickerValue(to)}
                onChange={value => onDateRangeChange(from, dateValueToIso(value) ?? '')}
                startDate={from || undefined}
                placeholder="日付を入力"
                className="w-[190px]"
              />
            </div>
            {/* No 同期状態 filter: BD §2 検索条件 lists three conditions — the tab, 受信日, and 未読のみ.
                同期状態 is a COLUMN (BD 13.0), not a search condition. The API still accepts `syncState`
                and it is still covered by tests, so restoring the control is one component if BD adds it. */}
            <Checkbox checked={unreadOnly} onChange={e => onUnreadOnlyChange(e.target.checked)}>
              未読のみ
            </Checkbox>
          </div>

          <SyncNotificationTable
            // The tab is not a filter: switching inbox is choosing WHICH mailbox to read, not searching in one.
            hasFilters={!!(unreadOnly || from || to)}
            data={data}
            onRetry={onRetry}
            onMarkRead={onMarkRead}
            onDownloadCsv={onDownloadCsv}
            downloadingId={downloadingId}
            disabled={isBusy}
            retryingAggregateId={retryingAggregateId}
            readingId={readingId}
            isLoading={isLoading}
            showSiteColumn={showSiteColumn}
          />

          <div className="flex justify-end">
            <AppPagination
              current={current}
              total={total}
              pageSize={pageSize}
              onChange={onChange}
            />
          </div>
        </div>
      </main>
    </AppContainer>
  );
};

export default AdminSyncNotificationListTemplate;
